import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Tabs, Tooltip, Spin } from 'antd';
import { useAsync } from 'react-use';
import { useDashboardFilterContext } from 'views/Dashboard/ImplementData/components/Filter/useFilter';
import { ctx2FetchParam, percentDisplay } from 'views/Dashboard/ImplementData/utils';
import {
  getRobotCommentContribution,
  getRobotCancelOrderContribution,
  getRobotPaymentContribution,
  getRobotOrderContribution,
  getRobotReplyContribution,
} from 'services/api/dashboard';
import { useTranslate } from '@tolgee/react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { What } from 'views/Marketing/components/PolicyTable';
import RobotReminderContribution from './ReminderOrder';
import RobotPaymentContribution from './Payment';
import RobotResolveCancelContribution from './ResolveCancel';
import RobotReminderCommentContribution from './ReminderComment';
import RobotReplayCommentContribution from './ReplayComment';
import { convertRobotContributionData } from '../converData';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useOrgConfigQAEnable } from 'hooks/business/useOrgConfig';

const Container = styled.div`
  width: 100%;
  margin-top: 16px;
  background-color: #ffffff;
  .ant-tabs-nav {
    .ant-tabs-ink-bar {
      top: 0;
    }
    padding: 0;
    .ant-tabs-nav-wrap {
      .ant-tabs-nav-list {
        flex: 1;
        .ant-tabs-tab {
          flex: 1;
          padding: 0;
          background: #fafafa;
          border-right: 1px solid rgba(240, 242, 245, 1);
          margin-left: 0;
          &.ant-tabs-tab-active {
            background-color: #ffffff;
          }
        }
        .ant-tabs-tab-btn {
          width: 100%;
        }
      }
    }
  }
  .ant-tabs-nav-operations {
    display: none !important;
  }
  .ant-tabs-tab-active {
    .title {
      color: #3366ff;
    }
  }
`;
const Tab = styled.div`
  padding: 14px 16px;
  .title {
    color: #515359;
  }
  .sub-title {
    color: #84868c;
    font-size: 12px;
    margin-top: 10px;
    svg {
      margin-left: 4px;
    }
  }
  .percent {
    color: #515359;
    font-size: 18px;
  }
`;

const RobotContribution = () => {
  const { filter: filterCtx, platforms } = useDashboardFilterContext();
  const { t } = useTranslate();
  const qaEnable = useOrgConfigQAEnable();
  const fetchParams = useMemo(() => {
    return {
      ...ctx2FetchParam(filterCtx),
      qaIs: qaEnable,
    };
  }, [filterCtx, qaEnable]);

  const { value, loading } = useAsync(
    () =>
      Promise.allSettled(
        [
          getRobotOrderContribution,
          getRobotPaymentContribution,
          getRobotCancelOrderContribution,
          getRobotCommentContribution,
          getRobotReplyContribution,
        ].map((fn) => fn(fetchParams)),
      ).then(convertRobotContributionData),
    [fetchParams],
  );

  return (
    <Container>
      <Spin spinning={loading}>
        <Tabs animated>
          <Tabs.TabPane
            key="催单"
            tab={
              <Tab>
                <div className="title">{t('ViewDashboard.机器人催单贡献')}</div>
                <div className="sub-title">
                  <span>{t('ViewDashboard.催单后下单买家比例')}</span>
                  <Tooltip title={t('ViewDashboard.机器人催单贡献tips')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="percent">{percentDisplay(value?.机器人贡献.催单)}</div>
              </Tab>
            }
          >
            <RobotReminderContribution data={value?.机器人催下单每日} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key="催付款"
            tab={
              <Tab>
                <div className="title">{t('ViewDashboard.机器人催付贡献')}</div>
                <div className="sub-title">
                  <span>{t('ViewDashboard.机器人催付贡献.催付后付款比例')}</span>
                  <Tooltip title={t('ViewDashboard.机器人催付贡献tips')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="percent">{percentDisplay(value?.机器人贡献.催付)}</div>
              </Tab>
            }
          >
            <RobotPaymentContribution data={value?.机器人催付款每日} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key="取消订单挽回"
            tab={
              <Tab>
                <div className="title">{t('ViewDashboard.机器人取消订单挽回贡献')}</div>
                <div className="sub-title">
                  <span>{t('ViewDashboard.取消订单挽回率')}</span>
                  <Tooltip title={t('ViewDashboard.机器人取消订单挽回贡献tips')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="percent">{percentDisplay(value?.机器人贡献.取消订单挽回)}</div>
              </Tab>
            }
          >
            <RobotResolveCancelContribution data={value?.机器人取消订单挽回每日} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key="催好评"
            tab={
              <Tab>
                <div className="title">
                  {t('ViewDashboard.机器人催好评贡献')}
                  {(platforms.includes('TIKTOK') || platforms.includes('TIKTOK_US')) &&
                    (filterCtx.platform === 'ALL' || isTiKTokUpperCase(filterCtx.platform)) && (
                      <Tooltip title={t('ViewDashboard.机器人催好评贡献titleTips')}>
                        <What />
                      </Tooltip>
                    )}
                </div>
                <div className="sub-title">
                  <span>{t('ViewDashboard.催好评转化率')}</span>
                  <Tooltip title={t('ViewDashboard.机器人催好评贡献tips')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="percent">{percentDisplay(value?.机器人贡献.催好评)}</div>
              </Tab>
            }
          >
            <RobotReminderCommentContribution data={value?.机器人催好评每日} />
          </Tabs.TabPane>
          <Tabs.TabPane
            key="自动回评"
            tab={
              <Tab>
                <div className="title">{t('ViewDashboard.机器人回评贡献')}</div>
                <div className="sub-title">
                  <span>{t('ViewDashboard.自动回评率')}</span>
                  <Tooltip title={t('ViewDashboard.机器人回评贡献tips')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="percent">{percentDisplay(value?.机器人贡献.自动回评)}</div>
              </Tab>
            }
          >
            <RobotReplayCommentContribution data={value?.机器人自动回评每日} />
          </Tabs.TabPane>
        </Tabs>
      </Spin>
    </Container>
  );
};

export default RobotContribution;
